/**
 * EditorialAdminInline — small floating toolbar shown on a Topic page
 * when an admin operator token is present in sessionStorage. Lets the
 * Owner / admin:
 *   • Run `Generate Editorial v1` (calls the AI, persists modules).
 *   • Toggle individual module visibility (show/hide per dossier).
 *
 * Stays out of the way for normal readers — only renders if the
 * `archive-admin-token` session key is set. Backend still enforces the
 * gate; this is a UX convenience.
 */
import React, { useState } from "react";
import { Loader2, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { adminEditorialGenerate, adminEditorialPatch } from "../../lib/api";
import { MediaAttachButton } from "../media/MediaAttachButton";

const MODULE_KEYS = [
  ["redacted",        "Redacted"],
  ["editorial_notes", "Editorial"],
  ["timeline",        "Timeline"],
  ["evidence",        "Evidence"],
  ["sources",         "Sources"],
  ["reliability",     "Reliability"],
  ["unresolved",      "Unresolved"],
  ["related",         "Related"],
  ["footer",          "Footer"],
];

export const EditorialAdminInline = ({ slug, modules, onUpdated }) => {
  const token = (typeof window !== "undefined")
    ? sessionStorage.getItem("archive-admin-token")
    : null;
  const [busy, setBusy] = useState(false);
  if (!token) return null;

  const visible = (modules && modules.modules_visible) || {};

  const generate = async () => {
    if (busy) return;
    if (!window.confirm(
      "Generate Editorial Standard v1 dossier?\n\n" +
      "This calls the AI and replaces any existing editorial_modules on this topic."
    )) return;
    setBusy(true);
    try {
      const res = await adminEditorialGenerate(token, slug);
      toast.success("Editorial v1 dossier generated.");
      onUpdated?.(res.editorial_modules);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const toggle = async (key) => {
    if (busy) return;
    const next = { ...visible, [key]: visible[key] === false ? true : false };
    setBusy(true);
    try {
      const res = await adminEditorialPatch(token, slug, { modules_visible: next });
      onUpdated?.(res.editorial_modules);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="editorial-admin-inline"
      className="my-6 border border-[#D92D20]/40 bg-[#0F0F0F] p-3"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
        <Wand2 size={11} /> // EDITORIAL · v1 · ADMIN
        {modules?.generated_at && (
          <span className="text-[#757065] normal-case tracking-normal text-[10px]">
            · generated {modules.generated_at.slice(0, 16).replace("T", " ")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          data-testid="editorial-admin-generate"
          className="inline-flex items-center gap-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {modules ? "Regenerate" : "Generate"} Editorial v1
        </button>
        <span className="font-mono text-[10px] text-[#757065]">
          {modules?.sources?.length || 0} sources · {modules?.timeline?.length || 0} events · {modules?.evidence?.length || 0} evidence
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1 border-t border-[#2D2A26]">
        {MODULE_KEYS.map(([k, label]) => {
          const on = visible[k] !== false;
          return (
            <button
              key={k}
              type="button"
              disabled={busy || !modules}
              onClick={() => toggle(k)}
              data-testid={`editorial-admin-toggle-${k}`}
              className={
                "font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 border " +
                (on
                  ? "border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e]"
                  : "border-[#454038] text-[#757065] hover:text-[#A39E93]") +
                ((busy || !modules) ? " opacity-50 cursor-not-allowed" : "")
              }
              title={`${on ? "Hide" : "Show"} ${label} module`}
            >
              {on ? "●" : "○"} {label}
            </button>
          );
        })}
      </div>
      <div className="mt-2 pt-2 border-t border-[#2D2A26]">
        <MediaAttachButton token={token} targetType="dossier" targetId={slug} recordName={slug} />
      </div>
    </div>
  );
};

export default EditorialAdminInline;
